import { Map } from 'immutable';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { absolute } from '../../components/AnimatedRoutes';
import { getImageUrl } from '../../../../helpers';
import { submitAnswer, submitError as onSubmitError } from '../../../../ducks/question';
import BackgroundImage from '../../components/BackgroundImage';
import Content from '../../components/Content';
import Flash from '../../components/Flash';
import Header from '../../components/Header';
import styles from './question.scss';
import withUser from '../../../../components/withUser';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    stackPop: PropTypes.func.isRequired,
  }).isRequired,
  question: ImmutablePropTypes.map,
  dispatch: PropTypes.func.isRequired,
  submitted: PropTypes.bool,
  submitError: PropTypes.shape({
    response: PropTypes.shape({
      status: PropTypes.string,
    }),
  }),
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  awaitingClaim: PropTypes.arrayOf(PropTypes.shape({
    question_id: PropTypes.string.isRequired,
    answer_id: PropTypes.string.isRequired,
  })),
};

const errorMessages = (code) => {
  switch (code) {
    case 409:
      return 'You\'ve already answered this question.';
    case 408:
      return 'The question is closed for answers.';
    default:
      return 'Something went wrong and we\'re not sure why.';
  }
};

class Question extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      percentage: 0,
    };

    this.startTimer = null;
    this.latency = undefined;
  }

  componentDidMount() {
    // Start the timer and let it run always
    this.startTimer = setInterval(this.tick, 100);
  }

  componentWillUnmount() {
    clearInterval(this.startTimer);
  }

  onSubmitAnswer = (id) => () => {
    const {
      dispatch,
      question,
      user,
      awaitingClaim,
    } = this.props;

    const alreadyAnswered = awaitingClaim.find((reply) => (
      reply.question_id === question.get('id')
    ));

    if (!user && alreadyAnswered) {
      dispatch(onSubmitError({ response: { status: 409 } }));
    } else {
      dispatch(submitAnswer(question.get('id'), id, user));
      this.setState({
        chosenAnswer: id,
      });
    }
  }

  tick = () => {
    const { question } = this.props;

    if (Map.isMap(question)) {
      const {
        time_end,
        time_start,
        server_timestamp,
      } = question.toJS();

      // Calculate and save timediff from client -> server
      if (this.latency === undefined) {
        const latency = moment().diff(Number(server_timestamp));
        this.latency = latency;
      }

      const timeEnd = moment.utc(time_end);

      if (timeEnd.isAfter(moment().add(this.latency, 'ms'))) {
        const duration = moment(time_end) - moment(time_start);
        const timeLeft = moment().add(this.latency).diff(timeEnd);
        // If it's less than 100 ms left, set it to 0
        const percentage = -timeLeft > 100 ? -timeLeft / duration : 0;
        this.setState({ percentage });
      }
    }
  };

  render() {
    const {
      question,
      submitted,
      submitError,
      history,
    } = this.props;

    const {
      percentage,
      chosenAnswer,
    } = this.state;

    const errorMessage = submitError && submitError.response
      && errorMessages(submitError.response.status);

    const totalReplies = (question ? question.get('answers') : Map())
      .reduce((acc, answer) => acc + parseInt(answer.get('replies'), 10), 0);

    const showResults = !!(question && question.get('correct_answers_and_results'));
    const hasCorrect = showResults && question.get('correct_answers_and_results')
      && question.get('correct_answers_and_results').toJS().find((answer) => answer.correct);

    return (
      <>
        <Header
          onBack={() => history.stackPop()}
        >
          Question
        </Header>
        <Flash
          title="High five!"
          message={submitted && 'Your answer is locked in!'}
          neutral
        />
        <Flash
          title="Woops!"
          message={submitError && errorMessage}
          error
        />
        <Content>
          { question
            && (
              <>
                <div className={styles.countdown}>
                  <div
                    className={styles.timeLeft}
                    style={{
                      transform: `scale3d(${percentage}, 1, 1)`,
                    }}
                  />
                </div>
                <div className={styles.question}>
                  <div className={styles.wrapper}>
                    <div className={styles.title}>
                      { question.get('image_id')
                      && (
                      <BackgroundImage
                        className={styles.questionImage}
                        src={getImageUrl(question.get('image_id', 'thumb_300'))}
                      />
                      )}
                      <div>{question.get('question')}</div>
                    </div>
                    <div className={styles.answers}>
                      { question.has('answers') && question.get('answers').map((answer) => (
                        <BackgroundImage
                          key={answer.get('id')}
                          disabled={submitted}
                          onClick={this.onSubmitAnswer(answer.get('id'))}
                          className={classNames(
                            styles.answer,
                            { [styles.imageAnswerContainer]: answer.get('image_id') && true },
                            { [styles.disabled]: submitted },
                            { [styles.incorrect]: hasCorrect && !answer.get('correct') },
                            { [styles.chosen]: answer.get('id') === chosenAnswer },
                            { [styles.correct]: hasCorrect && answer.get('correct') },
                          )}
                          src={answer.get('image_id') && getImageUrl(answer.get('image_id'), 'thumb_300')}
                          role="button"
                          tabIndex={0}
                        >
                          { answer.has('answer')
                          && (
                          <div className={classNames({
                            [styles.imageAnswer]: answer.get('image_id') && true,
                          })}
                          >
                            <div>{answer.get('answer')}</div>
                            {answer.has('replies')
                              && (
                              <div className={styles.result}>
                                {Math.round((answer.get('replies') / totalReplies) * 100)}
                                %
                              </div>
                              )}
                          </div>
                          )}
                        </BackgroundImage>
                      )).toArray()}
                    </div>
                  </div>
                </div>
              </>
            )}
        </Content>
      </>
    );
  }
}

Question.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    question: state.getIn(['question', 'data']),
    loading: state.getIn(['question', 'loading']),
    error: state.getIn(['question', 'error']),
    submitted: state.getIn(['question', 'submitted']),
    submitting: state.getIn(['question', 'submitting']),
    submitError: state.getIn(['question', 'submitError']),
    awaitingClaim: state.getIn(['question', 'awaitingClaim']),
  };
}

export default withUser(connect(mapStateToProps)(absolute(Question)));
